<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxRoot, ComboboxTrigger, ComboboxCancel, ComboboxViewport } from 'radix-vue'
import { cn } from '@pkg/ui/lib/utils';
import { useThrottleFn } from '@vueuse/core';
import MagnifyingGlass from '@pkg/cms-icons/icons/Magnifying_Glass2_Icon.svg';
import type { Origin } from '@pkg/types/surpriseBag';
import { Loader2 } from 'lucide-vue-next';

const v = ref<Origin>()
const anchorRef = ref<HTMLElement>();
const comboboxOpen = ref(false);

const props = defineProps<{
  placeholder?: string | undefined;
  options: Origin[];
  loading?: boolean;
  noLocationText: string;
  initialSearchValue?: string;
  headerAutocomplete?: boolean;
  textColor?: string;
  border?: boolean;
  lightIcon?: boolean;
  inputAriaLabel?: string;
}>();

const showResults = computed(() => props.options.length > 0);
const searchValue = ref('');
const clearInputActive = ref(false);

const resultsListWidth = computed(() => `${anchorRef?.value?.offsetWidth}px` || 'auto');

// Sync searchValue with initialSearchValue prop (from URL params)
watch(() => props.initialSearchValue, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    searchValue.value = newValue;
    clearInputActive.value = true;
  }
}, { immediate: true });
const showNoLocationText = computed(() => !showResults.value && !props.loading);

onMounted(() => {
  const input = document.getElementById('autocomplete-main-input');
  if(input && input.parentElement) {
    input.parentElement.ariaHidden = 'false';
  }
})

const emit = defineEmits<{
  selected: [value: Origin | undefined];
  inputChange: [value: string];
}>();

const emitSearch =  useThrottleFn((event: Event | string) => {
  emit('inputChange', searchValue.value);
}, 400);

const handleInputChange = (event: Event | string) => {
  if (typeof event === 'string') {
    searchValue.value = event;
  } else if (event && event.target && 'value' in event.target) {
    searchValue.value = (event.target as HTMLInputElement).value;
  }
  clearInputActive.value = (typeof searchValue.value === 'string' && searchValue.value.trim().length > 0) ? true : false;
  emitSearch(event);
};

const handleInput = (option: Origin) => {
  v.value = option;
  searchValue.value = option.displayName;
  emit('selected', option);
}

const cancelHandler = () => {
  searchValue.value = '';
  v.value = undefined;
  comboboxOpen.value = false;
  emit('selected', undefined);
  emit('inputChange', '');
  clearInputActive.value = false;
};

</script>

<template>
  <ComboboxRoot v-model="v" v-model:open="comboboxOpen">
  <div ref="anchorRef" class="relative w-full max-w-md min-w-0">
    <ComboboxAnchor 
      :class="cn({ 'header-autocomplete header-autocomplete__wrapper header-autocomplete-input' : headerAutocomplete && !border,'s-autocomplete__border': border })"
      class="h-[40px] w-full bg-[color:theme('colors.neutral.10')] flex items-center focus:border-[color:var(--input-active-border)] disabled:cursor-not-allowed disabled:opacity-50 border-[color:var(--popper-hover-text-color)]
        rounded-md text-[color:var(--popper-hover-text-color)] whitespace-nowrap [&>span]:truncate [&>span]:min-w-0 pl-8"
    >
      <ComboboxTrigger>
        <img id="autocomplete-icon" :src="MagnifyingGlass" alt="Magnifying glass icon" :class="cn('w-16 h-16', { 'header-autocomplete-icon': headerAutocomplete, 'header-autocomplete-icon-light': !lightIcon, 'header-autocomplete-icon-dark': lightIcon })"/>
      </ComboboxTrigger>
      <ComboboxInput
        autocomplete="off"
        :model-value="v"
        :aria-label="props.inputAriaLabel"
        @input="handleInputChange"
        :class="[cn({ 'header-autocomplete__wrapper' : headerAutocomplete }, `inline-flex w-full items-center justify-between border px-[12px] py-[8px] text-sm
          outline-none bg-[color:white] border-none rounded-md focus:bg-white disabled:cursor-not-allowed disabled:opacity-50`)]"
        :placeholder="props.placeholder"
        id="autocomplete-main-input"
        :value="searchValue"
      />
      <ComboboxCancel
        id="autocomplete-cancel-button"
        v-show="clearInputActive"
        class="w-32 h-32 hover:opacity-50"
        @click="cancelHandler"
      >
        <img
          width="24"
          height="24"
          alt="Cancel Icon"
          src="data:image/svg+xml,%3Csvg%20width%3D'24'%20height%3D'24'%20viewBox%3D'0%200%2024%2024'%20fill%3D'none'%20xmlns%3D'http%3A//www.w3.org/2000/svg'%3E%3Cpath%20d%3D'M12%2013.0538L6.92689%2018.1269C6.78844%2018.2654%206.6144%2018.3362%206.40479%2018.3394C6.19519%2018.3426%206.01795%2018.2718%205.87309%2018.1269C5.7282%2017.982%205.65576%2017.8064%205.65576%2017.6C5.65576%2017.3936%205.7282%2017.218%205.87309%2017.0731L10.9462%2012L5.87309%206.92689C5.73462%206.78844%205.66379%206.6144%205.66059%206.40479C5.65737%206.19519%205.7282%206.01795%205.87309%205.87309C6.01795%205.7282%206.19359%205.65576%206.39999%205.65576C6.60639%205.65576%206.78202%205.7282%206.92689%205.87309L12%2010.9462L17.0731%205.87309C17.2115%205.73462%2017.3856%205.66379%2017.5952%205.66059C17.8048%205.65737%2017.982%205.7282%2018.1269%205.87309C18.2718%206.01795%2018.3442%206.19359%2018.3442%206.39999C18.3442%206.60639%2018.2718%206.78202%2018.1269%206.92689L13.0538%2012L18.1269%2017.0731C18.2654%2017.2115%2018.3362%2017.3856%2018.3394%2017.5952C18.3426%2017.8048%2018.2718%2017.982%2018.1269%2018.1269C17.982%2018.2718%2017.8064%2018.3442%2017.6%2018.3442C17.3936%2018.3442%2017.218%2018.2718%2017.0731%2018.1269L12%2013.0538Z'%20fill%3D'%23859494'/%3E%3C/svg%3E" />
      </ComboboxCancel>
    </ComboboxAnchor>
    <ComboboxContent v-if="showNoLocationText" :class="[cn(`overflow-y-auto bg-[theme('colors.neutral.10')] border-[color:var(--popper-hover-text-color)]
        rounded-md pl-16 pr-24 pb-16`), 's-autocomplete__list-wrapper', showNoLocationText ? 'block' : 'hidden', { 'header-autocomplete__content': headerAutocomplete }]"
        :style="{ width: `${anchorRef?.offsetWidth}px` || 'auto'}">
        <p class="s-autocomplete__empty">{{ noLocationText }}</p>
    </ComboboxContent>
    <ComboboxContent v-if="showResults" :class="[cn(`overflow-y-auto bg-[theme('colors.neutral.10')] border-[color:var(--popper-hover-text-color)]
        rounded-md pl-16 pr-24 pb-16`), 's-autocomplete__list-wrapper', showResults ? 'block' : 'hidden', { 'header-autocomplete__content': headerAutocomplete }]"
        :style="{ width: resultsListWidth }">
        <Loader2 v-if="loading" class="m-24 w-32 h-32 mr-8 animate-spin self-center color:[var(--theme-heading-color-app)]" />
        <ComboboxViewport v-if="!loading"  class="p-[5px]">
          <ComboboxItem
            v-for="(option, index) in options"
            :key="`${option.displayName}-${index}`"
            :class="cn(`relative flex w-full cursor-default select-none items-center py-8 pr-8
              text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50
              text-[color:var(--theme-text-color-app)] hover:bg-[color: var(--theme-background-color)]
              hover:text-[color:var(--theme-heading-color-app)] hover:font-bold hover:cursor-pointer border-b border-[color:theme('colors.neutral.30')]
              focus:text-[color:var(--theme-heading-color-app)] focus:bg-[color: var(--theme-background-color)] focus:font-bold focus:cursor-pointer` )"            
            :value="option"
            @click="handleInput(option)"
          >{{ option.displayName }}</ComboboxItem>
        </ComboboxViewport>
    </ComboboxContent>
  </div>
  </ComboboxRoot>
</template>

<style scoped>

div[data-highlighted] {
  color: var(--theme-heading-color-app) !important;
  font-weight: bold;
}
.s-autocomplete {
  &__empty {
    display: flex;
    justify-content: center;
    padding: theme('spacing.32') theme('spacing.32') theme('spacing.16') theme('spacing.32');
    text-align: center;
    line-height: 2;
    width: 100%;
  }

  &__border {
    border: 1px solid theme('colors.neutral.50');
    border-radius: 100px;
    padding: theme('spacing.0') theme('spacing.16');
    width: 90vw;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    background: transparent;

    @screen tablet-wide {
      width: 45vw;
    }
    #autocomplete-icon {
      filter: brightness(0) invert(0);
    }
    #autocomplete-cancel-button {
      filter: brightness(0) invert(0);
    }

    input {
      background: transparent !important;
      color: theme('colors.black');
      &::placeholder {
        color: theme('colors.black');
      }
    }
  }

  &__list-wrapper {
    z-index: 9;
    margin-top: theme('spacing.4');
    position: absolute;
    box-shadow:  theme('spacing.0') 1px  theme('spacing.8')  theme('spacing.0') rgba(37, 45, 45, 0.2);

    &:deep(.wrapper){
      padding-left: theme('spacing.0');
      padding-right: theme('spacing.0');
    }
  }
}
  
  .header-autocomplete {
    color: inherit !important;
  
    &__wrapper {
      background-color: transparent !important;
    }

    &__content {
      @screen desk {
        overflow: visible;
        margin-top: theme('spacing.16');
        &::before {
          content: '';
          position: absolute;
          left: 15%;
          transform: translateX(-50%) rotate(45deg);
          width: 1.2rem;
          height: 1.2rem;
          background-color: theme('colors.neutral.10');
          border-radius: 4px 0 0 0;
          z-index: 10;
          pointer-events: none;
          top: -0.6rem;
        }
      }
    }
  
  
    &:hover {
      margin-right: theme('spacing.16');
      /* glassmorphism effect like liquid-glass button */
      background: rgba(255, 255, 255, 0.2) !important;
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(5px);
      -webkit-backdrop-filter: blur(5px);
      border: 1px solid rgba(255, 255, 255, 0.3) !important;
    }
  
    &-input {
      border-radius: 100px;
      max-width: 130px;
      padding-right: theme('spacing.8');
      
      &:hover {
        transition: all 0.3s ease;
        max-width: 170px;
      }
    }
  
    &-icon {
      &-light {
        filter: invert(18%) sepia(96%) saturate(2000%) hue-rotate(165deg) brightness(50%) contrast(105%);
      }
      
      &-dark {
        filter: brightness(0) invert(1);
      }
    }
  
    input {
      background: transparent !important;
      color: inherit !important;
  
      &::placeholder {
        color: inherit !important;
      }
    }
  }
  
  </style>